/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useResourcesNewQuery } from "~/generated/graphql";
import { formatMoneyWithFallback, Merge } from "~/tools";
import { Estate } from "../../components/types";

type EstateRecordApiResponse = NonNullable<
  ReturnType<typeof useResourcesNewQuery>["data"]
>;
type EstateRecordApiItem = NonNullable<
  NonNullable<EstateRecordApiResponse["estateV2"]>["resources"]
>[number];

type EstateRecord = Merge<EstateRecordApiItem>;

export function mapEstateRecord(
  estate: EstateRecordApiItem,
  currency: string
): Estate {
  // Cast API estate record to our merged estate record to simplify mapping
  // Merged estate record serves as intersection type of all types in union
  // It is type-safe operation as all merged fields are optional
  const estateRecord: EstateRecord = estate as EstateRecord;
  const actualCost = getActualCost(estate.__typename, estateRecord);

  return {
    id: estateRecord.id,
    provider: estateRecord.provider,
    providerId: estateRecord.providerId,
    type: estateRecord.providerType ?? undefined,
    category: estateRecord.category ?? undefined,
    nordcloudId: estateRecord?.id,
    forecast: formatMoneyWithFallback(
      estateRecord.cost?.forecast,
      currency,
      "Unavailable"
    ),
    cloudAccountId: estateRecord.cloudAccountId ?? "",
    actualCosts: formatMoneyWithFallback(actualCost, currency),
    assignments: estateRecord.assignments ?? undefined,
    environment: estateRecord.environment ?? undefined,
    isWaste: estateRecord.cloudWaste?.isWaste ?? false,
    name: estateRecord.name ?? estateRecord.providerType ?? "",
    reason: estateRecord.cloudWaste?.reason ?? "",
    region: estateRecord.region || "global",
    savingsAmount: estateRecord.savingsAmount?.amount ?? "",
    savingsAmountCount: estateRecord.savingsAmount?.count ?? 0,
    co2e: estateRecord.co2e?.currentMonth ?? "0",
    co2eSavings: estateRecord.savingsAmount?.co2e || "0",
  };
}

function getActualCost(type: string | undefined, estateRecord: EstateRecord) {
  return type === "CostResource"
    ? estateRecord.searchCost
    : estateRecord.cost?.currentMonth;
}
